import "./style.scss";

import { Breadcrumbs, CrumbClickFunc } from "@amzn/stencil-react-components/breadcrumbs";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Container, Row, Spacer, View } from "@amzn/stencil-react-components/layout";
import { Hr } from "@amzn/stencil-react-components/layout";
import { WithModal } from "@amzn/stencil-react-components/modal";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { IconSize } from "@amzn/stencil-react-icons/icons/interfaces";
import { checkOsCompatibility, StringHelper } from "common/helpers";
import { handleOrderTeam as handleOrderTeamHelper } from "common/helpers/handleOrderTeam";
import { Paths } from "common/paths/paths";
import { PreCheckoutInformation } from "common/types";
import { AcmeIntegration } from "common/types/catalog-types";
import { PERMISSIONS } from "config";
import { useLists } from "hooks/ListsContext";
import parse from "html-react-parser";
import NoLogoImage from "img/no_logo_image.png";
import { getUserOS } from "lib/helpers";
import { UserPermissions } from "lib/services";
import { AppSenseService, CheckLicenseCapOutput } from "lib/services/app-sense-service";
import { IItemVersion, ItemDetails, ItemTypes } from "lib/types/items";
import { capitalize } from "lodash";
import moment from "moment";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { CheckoutState } from "view/Page/CheckoutPage/CheckoutPage";

import { useAssetReturns } from "../../../hooks/useAssetReturns";
import { isParentChildProduct } from "../../helpers";
import { getString } from "../../uistringlabels/uiStringUtils";
import {
  CFMModuleName,
  CustomerFeedback,
  CustomerFeedbackAlignment,
  CustomerFeedbackIconPosition,
} from "../CustomerFeedback";
import { EtaStatusIndicator } from "../EtaStatusIndicator/EtaStatusIndicator";
import licenses from "../LicenseSelect/figma-licenses.json";
import { License, LicenseSelect } from "../LicenseSelect/LicenseSelect";
import { ListActions } from "../ListActions/ListActions";
import { OrderNow } from "../OrderNow";
import { PageMainContainer } from "../PageMainContainer/PageMainContainer";
import { ProductDetailsBanners } from "../ProductDetailsBanner";
import { SelectChildSku, VersionObject } from "../SelectChildSku";
import { SubmitOrderModal } from "../SubmitOrderModal";
import { SubmitReturnModal } from "../SubmitReturnModal";
import { useProductDetailsPage } from "./useProductDetailsPage";

// Constant to validate if the PDP is for a figma product
const FIGMA = "figma";

const breadCrumbsMapping: { [key in ItemTypes]: string } = {
  [ItemTypes.SOFTWARE]: "/",
  [ItemTypes.HARDWARE]: Paths.hardwareSearch.url,
};

export interface LegacyProductDetailsPageProps {
  /**
   * If it is a software or hardware item, determines OrderNow button behavior
   */
  assetType: ItemTypes;

  /**
   * Item to render
   */
  item: ItemDetails;

  /**
   * Same as parent item info if the item does not have children or versions
   */
  defaultVersion: IItemVersion | undefined;

  /**
   * Empty if the item does not have children or versions
   */
  versions: IItemVersion[];

  /**
   * Updates the default version used to make the order
   * @param version The selected version in the radio/select
   */
  updateVersionSelected: (version: any) => void;
  preCheckoutInformation?: PreCheckoutInformation;
  fetchPreCheckoutData?: (token: string) => void;
}

export const LegacyProductDetailsPage: FunctionComponent<LegacyProductDetailsPageProps> = (
  props: LegacyProductDetailsPageProps
) => {
  const appSenseService = new AppSenseService();
  const userOS: string[] = getUserOS();
  const history = useHistory();
  const isOsCompatible = checkOsCompatibility(props.item.os, userOS, props.assetType, props.item.subtype);
  const [selectedVersion, setSelectedVersion] = useState(props.defaultVersion ?? undefined);
  const [isPriceOverwritten, setIsPriceOverwritten] = useState<boolean>(props.item.taxonomyId!.includes(FIGMA));
  const [amount, setAmount] = useState(isPriceOverwritten ? licenses[0].price : props.defaultVersion?.amount);
  const [licenseSelection, setLicenseSelection] = useState<License | null>(isPriceOverwritten ? licenses[0] : null);
  const [limitCap, setLimitCap] = useState<CheckLicenseCapOutput>({} as CheckLicenseCapOutput);
  const [hasOrderedItem, setHasOrderedItem] = useState<string | undefined>();
  const [isLicenseCapLoading, setIsLicenseCapLoading] = useState<boolean>(false);

  const { order, activeOrder, loadingOrders } = useProductDetailsPage(props.item.taxonomyId, selectedVersion);
  const { lists, getLists } = useLists();
  const toggleLists = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_LISTS);
  useEffect(() => {
    if (toggleLists) {
      getLists();
    }
  }, [getLists, toggleLists]);

  const {
    canReturn,
    returnAsset,
    isLoading: returnLoading,
    error: returnError,
    hasLicense,
    licenseType,
    returnStatus,
    inProgress,
    returnRequests,
    checkAssetStatus,
  } = useAssetReturns({
    itemId: selectedVersion?.taxonomyId || props.item.taxonomyId,
    productName: selectedVersion?.applinkProductName || props.item.applinkProductName,
    instanceName: selectedVersion?.applinkInstanceName || props.item.applinkInstanceName,
    skipValidation: !(selectedVersion?.isReturnable ?? props.item.isReturnable),
  });

  /**
   * Determines if the user has an effective license for UI purposes.
   * For Smartsheet products:
   * - licensed_user: treated as having license
   * - connected_user: treated as no license
   * - any future license types: default to connected_user behavior (no license)
   *
   * For other products: use hasLicense as-is
   */
  const getEffectiveLicenseStatus = (hasLicense: boolean, productName?: string, licenseType?: string) => {
    if (productName === "Smartsheet" && hasLicense) {
      return licenseType === "licensed_user";
    }
    return hasLicense;
  };

  const effectiveHasLicense = getEffectiveLicenseStatus(
    hasLicense,
    selectedVersion?.applinkProductName || props.item.applinkProductName,
    licenseType
  );

  const assetStatus = {
    hasLicense: effectiveHasLicense,
    returnStatus: {
      inProgress,
      returnRequests,
    },
  };

  const isLoadingAssetReturnStatus = returnLoading;

  // Parent-Child product detection and team access functionality
  const isParentChild = isParentChildProduct(props.versions);
  const productDownloadUrlValue = selectedVersion?.productDownloadUrl ?? props.item.productDownloadUrl;

  /**
   * Handles team access order requests for parent-child products
   * Uses the shared helper function to convert the selected team back to IItemVersion format
   * and updates the component state to trigger the standard order flow for the selected team
   *
   * @param selectedTeam - The team/version object selected by the user from the team access modal
   */
  const handleOrderTeam = (selectedTeam: VersionObject): void => {
    handleOrderTeamHelper(selectedTeam, props.versions, setSelectedVersion, props.updateVersionSelected);
  };

  useEffect(() => {
    checkPreviouslyOrder();
  }, [order]);

  useEffect(() => {
    if (!isPriceOverwritten) {
      setAmount(props.defaultVersion?.amount);
    }
  }, [props.defaultVersion]);

  useEffect(() => {
    checkLicenseCap(props.item.taxonomyId!, !props.item.requiresLicenseCapValidation!);
  }, [props.item.taxonomyId]);

  const checkPreviouslyOrder = async () => {
    if (order) {
      setHasOrderedItem(moment(order.requested?.on?.toString()).format("MM/DD/YYYY"));
    } else {
      setHasOrderedItem(undefined);
    }
  };

  const checkLicenseCap = async (taxonomyId: string, shouldSkipValidation: boolean) => {
    setIsLicenseCapLoading(true);
    try {
      const result: CheckLicenseCapOutput = await appSenseService.checkLicenseCap(taxonomyId, shouldSkipValidation);
      setLimitCap(result);
    } catch (error) {
      /**
       * possible errors:
       * 1. user alias not registered in AppSense table
       */
      // default values
      setLimitCap({
        isCapReached: false,
        closestLeaderWithExceededLimit: "",
      });
    } finally {
      setIsLicenseCapLoading(false);
    }
  };

  // This useEffect was moved up to be with the other useEffect hooks

  /* istanbul ignore next */
  const handleCrumbClick: CrumbClickFunc = (event, crumb) => {
    event.preventDefault();
    if (crumb.url) {
      history.push(crumb.url);
    }
  };

  /* istanbul ignore next */
  const handleChangeVersion = (newSelectedSku: any) => {
    setSelectedVersion(newSelectedSku);
    props.updateVersionSelected(newSelectedSku);
  };

  /**
   * Updates the selected license and related state values.
   *
   * @param {License | undefined} newLicense - The license to select, or undefined
   * @description When a license is provided, updates the amount to the license price,
   * marks the price as manually overwritten, and updates the license selection state.
   */
  const updateSelectedLicense = (newLicense?: License) => {
    if (newLicense) {
      setAmount(newLicense.price);
      setIsPriceOverwritten(true);
      setLicenseSelection(newLicense);
    }
  };

  if (!props.item) return <div>No item data</div>;

  const renderModal = ({ close }: { close: () => void }) => {
    return (
      <SubmitOrderModal
        close={close}
        preCheckoutData={props.preCheckoutInformation}
        fetchPreCheckoutData={props.fetchPreCheckoutData!}
        taxonomyId={props.item.taxonomyId!}
        name={props.item.name!}
        status={props.item.status}
        etaDays={props.item.etaDays}
        hasStock={props.item.hasStock}
        externalButtonText={props.item.externalButtonText}
        orderRedirectURL={props.item.orderRedirectUrl}
        orderWorkflow={props.item.orderWorkflow ?? ""}
        license={licenseSelection ?? undefined}
        isOsCompatible={isOsCompatible}
        acme={props.item.acme}
      />
    );
  };

  /* istanbul ignore next */
  const goToCheckoutPage = () => {
    const state = {
      item: props.item,
      defaultVersion: props.defaultVersion,
      versions: props.versions,
    } as CheckoutState;

    history.push({
      pathname: Paths.checkout.url,
      state,
    });
  };

  const displayListOfStrings = (strings: string[]) => {
    return strings.map((string) => StringHelper.capitalizeFirstLetter(string)).join(", ");
  };

  /**
   * Renders a modal dialog for confirming asset return
   * @param {Object} params - The parameters object
   * @param {() => void} params.close - Function to close the modal
   * @returns {JSX.Element} The rendered modal component
   */
  const renderReturnModal = ({ close }: { close: () => void }) => {
    const handleReturnConfirm = async () => {
      await returnAsset();
    };

    return (
      <SubmitReturnModal
        close={close}
        productName={props.item.name}
        onConfirm={handleReturnConfirm}
        isLoading={isLoadingAssetReturnStatus}
      />
    );
  };

  /**
   * Renders a button that triggers the return asset modal
   * Uses WithModal component to handle modal display logic
   * @returns {JSX.Element} A button wrapped in WithModal component
   */
  const displayReturnButton = () => {
    const hasReturnInProcess = assetStatus?.returnStatus?.inProgress;
    const buttonText = hasReturnInProcess
      ? getString("licenseReturn.pendingReturnButton")
      : getString("licenseReturn.returnButton");

    return (
      <WithModal renderModal={renderReturnModal}>
        {({ open }: { open: () => void }) => (
          <div className="return-button-container">
            <Button
              variant={ButtonVariant.Secondary}
              onClick={open}
              data-cy="return-asset-button"
              data-testid="return-asset-button"
              icon={isLoadingAssetReturnStatus ? <Spinner size={SpinnerSize.Small} /> : undefined}
              disabled={isLoadingAssetReturnStatus || !canReturn}
            >
              {buttonText}
            </Button>
          </div>
        )}
      </WithModal>
    );
  };

  const renderCFM = () => {
    const module =
      props.assetType === ItemTypes.HARDWARE ? CFMModuleName.hardwareProductDetails : CFMModuleName.productDetails;

    return (
      <View>
        <Text color={"neutral90"} fontSize={"T200"} fontWeight={"regular"}>
          {parse(props.item.description)}
        </Text>
        <Hr color="neutral30" />
        <CustomerFeedback
          moduleName={module}
          alignment={CustomerFeedbackAlignment.Left}
          position={CustomerFeedbackIconPosition.Left}
        />
      </View>
    );
  };

  const parentItemName = props.item.parentName ?? props.item.name;
  return (
    <WithModal renderModal={renderModal}>
      {({ open }: { open: () => void }) => (
        <>
          <PageMainContainer className="product-details" paddingBottom="S500">
            <View>
              <Container paddingBottom="S300" paddingTop="S300">
                <Breadcrumbs
                  crumbs={[
                    { title: "Home", url: "/" },
                    { title: capitalize(props.assetType), url: breadCrumbsMapping[props.assetType] },
                    { title: parentItemName },
                  ]}
                  onCrumbClick={handleCrumbClick}
                />
                <ProductDetailsBanners
                  limitCap={limitCap}
                  assetStatus={assetStatus}
                  isOsCompatible={isOsCompatible}
                  hasOrderedItem={hasOrderedItem}
                  productName={props.item.name}
                />
              </Container>
              <Row
                key={props.item.taxonomyId}
                gridGap="S700"
                alignItems="flex-start"
                className="product-description-box"
              >
                <Col className="column-left">
                  <div className="product-img-wrapper">
                    <img
                      data-details-logo="details-logo"
                      className="product-img"
                      src={props.item.logoUrl.trim() !== "" ? props.item.logoUrl : NoLogoImage}
                      alt={`${props.item.name} logo`}
                    />
                  </div>
                </Col>
                <Col className="column-center">
                  <Row justifyContent="space-between">
                    <Row padding={{ top: "S200" }}>
                      <Text fontSize="T200" className="product-title" data-cy="product-title">
                        {parentItemName}
                      </Text>
                    </Row>
                    {toggleLists && (
                      <ListActions taxonomyId={props.item.taxonomyId} lists={lists} allowedDomain="software" />
                    )}
                  </Row>

                  {props.item?.shortDescription && (
                    <div className="product-short-details" data-cy="product-short-details">
                      <Text
                        data-details-description="details-description"
                        fontSize="T100"
                        className="product-description"
                      >
                        {props.item.shortDescription}
                      </Text>
                    </div>
                  )}

                  <hr />

                  <Row columnGap={60} gridGap={0} justifyContent="space-between">
                    <Text className="product-property">
                      <label data-cy="os-title">{getString("legacyDetails.os")}</label>
                      <br />
                      <strong>{displayListOfStrings(props.item.os)}</strong>
                    </Text>

                    <Text className="product-property">
                      <label data-cy="publisher-title">{getString("legacyDetails.publisher")}</label>
                      <br />
                      <strong>{props.item.publisher}</strong>
                    </Text>

                    <Text className="product-property">
                      <label data-cy="category-title">{getString("legacyDetails.category")}</label>
                      <br />
                      <strong>{displayListOfStrings(props.item.categories)}</strong>
                    </Text>

                    <Text className="product-property">
                      <label data-cy="eta-title">{getString("legacyDetails.deliveryTimeframe")}</label>
                      <br />
                      <EtaStatusIndicator
                        etaDays={props.item.etaDays}
                        hasStock={props.item.hasStock}
                        size={IconSize.Small}
                      />
                    </Text>
                  </Row>
                  <Col>
                    {props.versions.length > 0 && (!isParentChild || !hasLicense) && (
                      <>
                        <Spacer height="S500" />
                        <Col data-testid="select-version-component" style={{ width: "100%" }}>
                          <SelectChildSku
                            itemChildVersions={props.versions}
                            onChange={handleChangeVersion}
                            selectedVersion={selectedVersion}
                            autoSelectFirstVersion={false}
                          />
                        </Col>
                      </>
                    )}
                    {isPriceOverwritten && (
                      <Col data-testid="select-license-component">
                        <Spacer height="S500" />
                        <LicenseSelect
                          updateSelectedLicense={updateSelectedLicense}
                          selectedLicense={licenseSelection}
                          licenses={licenses}
                        />
                      </Col>
                    )}
                  </Col>
                </Col>
                <Col className="column-right" width={"15%"} alignItems="flex-end">
                  {props.defaultVersion && (
                    <OrderNow
                      orderWorkflow={props.item.orderWorkflow}
                      externalButtonText={props.item.externalButtonText}
                      itemType={props.assetType}
                      orderNowHandler={open}
                      goToCheckoutHandler={goToCheckoutPage}
                      orderRedirectUrl={props.item.orderRedirectUrl || selectedVersion?.orderRedirectUrl || ""}
                      moreDetailsUrl={props.item.moreDetailsUrl || ""}
                      amount={amount}
                      status={props.defaultVersion?.status}
                      currency={props.defaultVersion?.currency}
                      hasReachedCap={limitCap.isCapReached}
                      isLoading={isLoadingAssetReturnStatus || loadingOrders || isLicenseCapLoading}
                      activeOrder={!!activeOrder}
                      acme={props.item.acme}
                      userOS={userOS}
                      isReturnable={selectedVersion?.isReturnable ?? props.item.isReturnable}
                      hasLicense={assetStatus?.hasLicense}
                      isLegacy={true}
                      isParentChildProduct={isParentChild}
                      availableTeams={props.versions}
                      onOrderTeam={handleOrderTeam}
                      returnButtonComponent={displayReturnButton()}
                      parentItemName={parentItemName}
                      productDownloadUrl={productDownloadUrlValue}
                    />
                  )}

                  {/* Only show return button if user has a license and not Parent-Child */}
                  {assetStatus?.hasLicense && !isParentChild && displayReturnButton()}
                </Col>
              </Row>
            </View>
          </PageMainContainer>

          <PageMainContainer
            className="about-product-view"
            paddingBottom="S500"
            paddingTop="S500"
            paddingHorizontal="S300"
          >
            <View>
              <Row
                key={props.item.taxonomyId}
                gridGap="S700"
                alignItems="center"
                className="product-description-box"
                data-cy="product-description-box"
              >
                <Col className="column-left"></Col>
                <Col className="column-center">{props.item.description && renderCFM()}</Col>
                <Col className="column-right"></Col>
              </Row>
            </View>
          </PageMainContainer>
        </>
      )}
    </WithModal>
  );
};
